/**
 * History + Insight API client — Phase 4.1 + 4.2.
 *
 * Narrow client over:
 *   - GET /v1/history/:child_id/insights      (InsightCardData)
 *   - GET /v1/history/:child_id?page=...     (HistoryPage)
 *
 * Same pattern as daily.ts / children.ts — thin wrapper around apiFetch
 * with type-safe response handling. The server is the source of truth.
 */

import { apiFetch } from './client';
import type {
  InsightCardData,
  HistoryPage,
  HistoryItem,
  HistoryRange,
} from '@/types/history';

// ----------------------------------------------------------------------------
// GET /v1/history/:child_id/insights
// ----------------------------------------------------------------------------

type GetInsightsResponse = { ok: true } & InsightCardData;

/**
 * Fetch the aggregated insight data for a child.
 *
 * 6 facts in a 30-day window (server default, not configurable Y1):
 *   - totalPrinted       count of approved & printed/shared
 *   - totalSelected      count of approved/selected worksheets
 *   - topTema            top 3 PAUD tema (or generic theme_label fallback)
 *   - activityCounts     selected worksheet count grouped by activity
 *   - topActivity        most frequently selected activity
 *   - fastest            fastest approve swipe (or null)
 *   - undoCount          accidental undo events
 *
 * Throws ApiError on 401 (no X-Device-Id) or 404 (cross-device child).
 */
export async function getInsights(childId: number): Promise<InsightCardData> {
  const response = await apiFetch<GetInsightsResponse>(
    `/v1/history/${childId}/insights`,
  );
  return {
    windowDays: response.windowDays,
    totalPrinted: response.totalPrinted,
    totalSelected: response.totalSelected ?? 0,
    topTema: response.topTema,
    activityCounts: response.activityCounts ?? [],
    topActivity: response.topActivity ?? null,
    fastest: response.fastest,
    undoCount: response.undoCount,
    generatedAt: response.generatedAt,
  };
}

// ----------------------------------------------------------------------------
// GET /v1/history/:child_id?page=1&pageSize=20&range=all
// ----------------------------------------------------------------------------

type GetHistoryResponse = { ok: true } & HistoryPage;

/**
 * Fetch one page of approved_worksheets for a child, newest first.
 *
 * @param childId   Child profile id
 * @param page      1-based page number (default 1)
 * @param pageSize  1-50, default 20
 * @returns HistoryPage with items + pagination metadata + hasMore flag
 */
export async function getHistory(
  childId: number,
  page = 1,
  pageSize = 20,
  range: HistoryRange = 'all',
): Promise<HistoryPage> {
  const response = await apiFetch<GetHistoryResponse>(
    `/v1/history/${childId}?page=${page}&pageSize=${pageSize}&range=${range}`,
  );
  return {
    items: response.items as HistoryItem[],
    page: response.page,
    pageSize: response.pageSize,
    totalCount: response.totalCount,
    activityCounts: response.activityCounts ?? [],
    statusCounts: response.statusCounts ?? { printed: 0, needsPrint: 0 },
    hasMore: response.hasMore,
  };
}
